// stc-configurator/src/utils/fontRegistry.js

export const SYSTEM_FONTS = [
  {
    source: 'system',
    family: 'System UI',
    cssFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
    cssUrl: null
  },
  {
    source: 'system',
    family: 'Arial',
    cssFamily: 'Arial, sans-serif',
    cssUrl: null
  },
  {
    source: 'system',
    family: 'Georgia',
    cssFamily: 'Georgia, serif',
    cssUrl: null
  },
  {
    source: 'system',
    family: 'Trebuchet MS',
    cssFamily: '"Trebuchet MS", sans-serif',
    cssUrl: null
  },
  {
    source: 'system',
    family: 'Tahoma',
    cssFamily: 'Tahoma, sans-serif',
    cssUrl: null
  },
  {
    source: 'system',
    family: 'Verdana',
    cssFamily: 'Verdana, sans-serif',
    cssUrl: null
  }
]

export const GOOGLE_FONTS = [
  {
    source: 'google',
    family: 'Inter',
    cssFamily: '"Inter", sans-serif',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;700&display=swap'
  },
  {
    source: 'google',
    family: 'DM Sans',
    cssFamily: '"DM Sans", sans-serif',
    cssUrl: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;700&display=swap'
  },
  {
    source: 'google',
    family: 'Source Sans 3',
    cssFamily: '"Source Sans 3", sans-serif',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@300;400;700&display=swap'
  },
  {
    source: 'google',
    family: 'Manrope',
    cssFamily: '"Manrope", sans-serif',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;700&display=swap'
  },
  {
    source: 'google',
    family: 'Plus Jakarta Sans',
    cssFamily: '"Plus Jakarta Sans", sans-serif',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;700&display=swap'
  },
  {
    source: 'google',
    family: 'Work Sans',
    cssFamily: '"Work Sans", sans-serif',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;700&display=swap'
  },
  {
    source: 'google',
    family: 'Nunito Sans',
    cssFamily: '"Nunito Sans", sans-serif',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;700&display=swap'
  },
  {
    source: 'google',
    family: 'Poppins',
    cssFamily: '"Poppins", sans-serif',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;700&display=swap'
  },
  {
    source: 'google',
    family: 'IBM Plex Sans',
    cssFamily: '"IBM Plex Sans", sans-serif',
    cssUrl: 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;700&display=swap'
  },
  {
    source: 'google',
    family: 'Karla',
    cssFamily: '"Karla", sans-serif',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Karla:wght@300;400;700&display=swap'
  },
  {
    source: 'google',
    family: 'Rubik',
    cssFamily: '"Rubik", sans-serif',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;700&display=swap'
  },
  {
    source: 'google',
    family: 'Merriweather',
    cssFamily: '"Merriweather", serif',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Merriweather:wght@300;400;700&display=swap'
  },
  {
    source: 'google',
    family: 'Lora',
    cssFamily: '"Lora", serif',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Lora:wght@300;400;700&display=swap'
  },
  {
    source: 'google',
    family: 'Libre Baskerville',
    cssFamily: '"Libre Baskerville", serif',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@300;400;700&display=swap'
  }
]

// Single URL to load all curated Google Fonts at weight 400 for dropdown preview
export const PREVIEW_STYLESHEET_URL = 'https://fonts.googleapis.com/css2?' +
  GOOGLE_FONTS.map(f => 'family=' + encodeURIComponent(f.family)).join('&') +
  '&display=swap'

// All fonts combined for the dropdown (system + google)
export const ALL_FONTS = [...SYSTEM_FONTS, ...GOOGLE_FONTS]

/**
 * Load a Google Fonts stylesheet into the document.
 * Resolves on success, or immediately if cssUrl is empty / already loaded / not a valid css2 URL.
 * Rejects only on stylesheet load failure (e.g. network error, Google Fonts blocked).
 */
const _loadedFontUrls = new Set()
export function loadGoogleFont (cssUrl) {
  if (!cssUrl || _loadedFontUrls.has(cssUrl)) return Promise.resolve()
  try {
    const url = new URL(cssUrl)
    if (url.hostname !== 'fonts.googleapis.com' || !url.pathname.startsWith('/css2')) return Promise.resolve()
  } catch (e) {
    return Promise.resolve()
  }
  _loadedFontUrls.add(cssUrl)

  return new Promise((resolve, reject) => {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = cssUrl
    link.onload = () => resolve()
    link.onerror = () => {
      _loadedFontUrls.delete(cssUrl)
      link.remove()
      reject(new Error('Font load failed'))
    }
    document.head.appendChild(link)
  })
}

/**
 * Parse a pasted Google Fonts input. Accepts either:
 * - A raw css2 URL: https://fonts.googleapis.com/css2?family=Inter:wght@300;400;700&display=swap
 * - A full <link> snippet (may include preconnect lines)
 *
 * Returns a font object { source, family, cssFamily, cssUrl } or null if invalid.
 */
export function parseGoogleFontsInput (input) {
  if (!input || typeof input !== 'string') return null

  const trimmed = input.trim()

  // Try to extract a fonts.googleapis.com/css2 URL
  let cssUrl = null

  // Check if the input itself is a URL
  if (trimmed.startsWith('https://fonts.googleapis.com/css2')) {
    cssUrl = trimmed
  } else {
    // Try to find the URL inside <link> tags — ignore preconnect links
    const hrefMatches = trimmed.match(/href="(https:\/\/fonts\.googleapis\.com\/css2[^"]*)"/g)
    if (hrefMatches) {
      for (const match of hrefMatches) {
        const url = match.replace(/^href="/, '').replace(/"$/, '')
        if (url.includes('/css2')) {
          cssUrl = url
          break
        }
      }
    }
  }

  if (!cssUrl) return null

  // Add weights 300, 400, 700 if wght (weight) axis isn't already specified
  let finalCssUrl = cssUrl
  const fontFamilySegment = cssUrl.split('&')[0]
  if (!fontFamilySegment.includes('wght')) {
    const axisMatch = fontFamilySegment.match(/family=([^:&]+):([^@]+)@([^&]+)/)
    if (axisMatch) {
      // Has other settings but no weight (e.g. ital@0;1) — combine with wght for each existing value
      const [full, name, axes, values] = axisMatch
      const expanded = values.split(';').flatMap(t => ['300', '400', '700'].map(w => t + ',' + w))
      finalCssUrl = cssUrl.replace(full, `family=${name}:${axes},wght@${expanded.join(';')}`)
    } else {
      // No settings at all — just add weights
      finalCssUrl = cssUrl.replace('&display=swap', ':wght@300;400;700&display=swap')
    }
  }

  // Parse the family name from the URL
  // URL format: ...css2?family=Inter:wght@300;400;700&display=swap
  // or: ...css2?family=Playfair+Display:ital,wght@0,400;1,700&display=swap
  try {
    const urlObj = new URL(cssUrl)
    const familyParam = urlObj.searchParams.get('family')
    if (!familyParam) return null

    // Family param may be "Inter:wght@300;400;700" or "Playfair+Display:ital,wght@..."
    // The name is everything before the first ":"
    const family = familyParam.split(':')[0].replace(/\+/g, ' ')
    if (!family) return null

    const cssFamily = `"${family}", sans-serif`

    return {
      source: 'google',
      family,
      cssFamily,
      cssUrl: finalCssUrl
    }
  } catch (e) {
    return null
  }
}
